import {ReactNode, useCallback} from "react";
import {Accept, FileRejection} from "react-dropzone";
import {UploadingFile} from "./FileUploader";
import Dropzone from "./Dropzone";
import {uniquifyFiles} from "./utils";
import {Icon, Link, Stack, Text} from "@chakra-ui/react";
import {CloudArrowUpIcon} from "@heroicons/react/24/outline";
import {FileUpload, FileUploadBase} from "../../hooks/fileUpload";
import T from "platformed-components/i18n/T";

export function MultiFileUpload<TFileUpload extends FileUploadBase = FileUpload>({
  value,
  onChange,
  accept,
  uploadConstructor,
  onDropRejected,
  existingNames,
}: {
  value: TFileUpload[];
  accept?: Accept;
  onChange: (value: TFileUpload[]) => void;
  uploadConstructor: (file: File) => TFileUpload;
  onDropRejected?: (files: FileRejection[]) => ReactNode;
  /** Names already taken outside this widget (e.g. existing records). Dropped
   *  files are auto-incremented to stay unique against these too, not just
   *  against files already in the widget. */
  existingNames?: readonly string[];
}) {
  const drop = useCallback(
    (acceptedFiles: File[]) => {
      const takenNames = [...(existingNames ?? []), ...value.map(upload => upload.file.name)];
      onChange([...value, ...uniquifyFiles(acceptedFiles, takenNames).map(file => uploadConstructor(file))]);
    },
    [onChange, value, uploadConstructor, existingNames],
  );

  const delete_ = useCallback(
    (fileUpload: FileUploadBase) => {
      onChange(value.filter(f => f !== fileUpload));
    },
    [value, onChange],
  );

  return (
    <Stack>
      {value.map((fileUpload, i) => (
        <UploadingFile key={i} fileUpload={fileUpload} onDelete={delete_} />
      ))}
      <Dropzone onDropAccepted={drop} onDropRejected={onDropRejected} accept={accept}>
        {isDragActive => (
          <Stack gap={2} align="center">
            <Icon
              as={CloudArrowUpIcon}
              display="block"
              fontSize="4xl"
              color={isDragActive ? "blue.500" : "gray.500"}
              mb="2"
              boxSize="1em"
            />
            <Text fontSize="sm">
              <T keyName="file_upload.drag_files_or_choose" params={{link: <Link color="blue.500" />}} />
            </Text>
          </Stack>
        )}
      </Dropzone>
    </Stack>
  );
}
